import { FunctionComponent, JSX, useEffect, useState } from 'react'
import { Daycare } from '@/types/Daycare'
import { useMutation } from '@apollo/client'
import styles from './notificationSuggest.module.scss'
import Title from '@/components/_custom/Title/title'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/modules/context/AuthContext'
import { ListItem, Table, TableBody, TableCell, TableRow } from '@mui/material'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { SET_NOTIFICATION_SUGGEST_SETTINGS } from '@/modules/API/daycare/daycareSuggestHandler'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import Checkbox from '@/components/_custom/Checkbox/Checkbox.tsx'
import { Add, Delete, Edit } from '@mui/icons-material'
import AddContactEmailDialog from './AddContactEmailDialog.tsx'
import EditContactEmailDialog from './EditContactEmailDialog.tsx'
import DeleteContactEmailDialog from './DeleteContactEmailDialog.tsx'

interface StaffSuggestProps {
    daycare: Daycare
    suggestContactEmails: string[]
    setSuggestContactEmails: (value: string[]) => void
    suggestNotification: boolean
    setSuggestNotification: (value: boolean) => void
    canUpdate: boolean
}

const NotificationSuggest: FunctionComponent<StaffSuggestProps> = ({
    daycare,
    suggestContactEmails,
    setSuggestContactEmails,
    suggestNotification,
    setSuggestNotification,
    canUpdate
}: StaffSuggestProps) => {
    const { t } = useTranslation()
    const { fetchCurrentDaycare } = useAuth()
    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [newContactEmails, setNewContactEmails] = useState<string[]>(suggestContactEmails)
    const [newNotification, setNewNotification] = useState<boolean>(suggestNotification)

    const [addDialogOpen, setAddDialogOpen] = useState<boolean>(false)
    const [editingEmail, setEditingEmail] = useState<string | null>(null)
    const [deletingEmail, setDeletingEmail] = useState<string | null>(null)

    useEffect(() => {
        setNewContactEmails(suggestContactEmails)
        setNewNotification(suggestNotification)
    }, [suggestContactEmails, suggestNotification])

    const [setNotificationSuggestSettings] = useMutation(SET_NOTIFICATION_SUGGEST_SETTINGS, {
        onCompleted: () => {
            fetchCurrentDaycare()
            showNotificationSuccess(t('suggestSettingsUpdated'))
        },
        onError: () => {
            showNotificationError(t('errorUpdatingSuggestSettings'))
        }
    })

    function handleNotificationChange(value: boolean): void {
        setNewNotification(value)
        setSuggestNotification(value)
        setNotificationSuggestSettings({
            variables: {
                daycareId: daycare.id,
                contactEmails: newContactEmails,
                notification: value
            }
        })
    }

    function handleEmailsUpdated(newEmails: string[]): void {
        setNewContactEmails(newEmails)
        setSuggestContactEmails(newEmails)
        fetchCurrentDaycare()
    }

    return (
        <div className={styles.StaffSuggestPlacement}>
            <div className={styles.Form}>
                <div className={styles.TitleAndButton}>
                    <Title text={t('notificationSettings')} className={styles.Title} />
                </div>
                <Table className={styles.SettingsTable}>
                    <TableBody className={styles.TableBody}>
                        {newContactEmails.length === 0 ? (
                            <TableRow className={styles.TableRow}>
                                <TableCell className={styles.RowTextAndButton} colSpan={2}>
                                    <div className={styles.RowTextAndButtonContent}>
                                        <p className={styles.Text}>{t('noContactEmails')}</p>
                                        <p className={styles.Text}>{t('noContactEmailsPart2')}</p>
                                        {canUpdate && (
                                            <ButtonTextIcon
                                                customIcon={<Add />}
                                                customText={t('addContactEmail')}
                                                type={'custom'}
                                                onClick={() => setAddDialogOpen(true)}
                                                className={styles.AddButtonWithMargin}
                                            />
                                        )}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : (
                            <>
                                <TableRow>
                                    <TableCell className={styles.LabelCell}>{t('contactEmails')}</TableCell>
                                    <TableCell className={styles.ChipsCell}>
                                        {[...newContactEmails]
                                            .sort((a: string, b: string): number => a.localeCompare(b))
                                            .map(
                                                (email: string): JSX.Element => (
                                                    <ListItem key={email} className={styles.ListItem}>
                                                        <div className={styles.ChipMenu}>
                                                            <span className={styles.ChipEmail}>{email}</span>
                                                            {canUpdate && (
                                                                <span className={styles.ChipIcons}>
                                                                    <Edit
                                                                        className={styles.ChipActionIcon}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation()
                                                                            setEditingEmail(email)
                                                                        }}
                                                                    />
                                                                    <Delete
                                                                        className={styles.ChipActionIcon}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation()
                                                                            setDeletingEmail(email)
                                                                        }}
                                                                    />
                                                                </span>
                                                            )}
                                                        </div>
                                                    </ListItem>
                                                )
                                            )}
                                    </TableCell>
                                </TableRow>
                                {canUpdate && newContactEmails.length < 5 && (
                                    <TableRow className={styles.TableRow}>
                                        <TableCell className={styles.RowTextAndButton} colSpan={2}>
                                            <div className={styles.RowTextAndButtonContent}>
                                                <ButtonTextIcon
                                                    customIcon={<Add />}
                                                    customText={t('addContactEmail')}
                                                    type={'custom'}
                                                    onClick={() => setAddDialogOpen(true)}
                                                    className={styles.AddButton}
                                                />
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </>
                        )}
                        {canUpdate && (
                            <TableRow className={styles.TableRow}>
                                <TableCell className={styles.RowText} colSpan={2}>
                                    <Checkbox
                                        text={t('wantSuggestionNotification')}
                                        checked={newNotification}
                                        onChange={canUpdate ? handleNotificationChange : setNewNotification}
                                    />
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            <AddContactEmailDialog
                open={addDialogOpen}
                handleClose={() => setAddDialogOpen(false)}
                daycareId={daycare.id}
                currentEmails={newContactEmails}
                notification={suggestNotification}
                onSuccess={handleEmailsUpdated}
            />
            {editingEmail && (
                <EditContactEmailDialog
                    open={!!editingEmail}
                    handleClose={() => setEditingEmail(null)}
                    daycareId={daycare.id}
                    currentEmails={newContactEmails}
                    notification={suggestNotification}
                    emailToEdit={editingEmail}
                    onSuccess={handleEmailsUpdated}
                />
            )}
            {deletingEmail && (
                <DeleteContactEmailDialog
                    open={!!deletingEmail}
                    handleClose={() => setDeletingEmail(null)}
                    daycareId={daycare.id}
                    currentEmails={newContactEmails}
                    notification={suggestNotification}
                    emailToDelete={deletingEmail}
                    onSuccess={handleEmailsUpdated}
                />
            )}
        </div>
    )
}

export default NotificationSuggest
